import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, applyDevOverride } from "@/context/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, setLoading } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login", { replace: true });
      return;
    }
    const sessionId = match[1];

    (async () => {
      try {
        const { data } = await api.post("/auth/session", { session_id: sessionId });

        // 1) Clear the OAuth hash BEFORE any state update so downstream
        //    re-renders don't re-trigger the hash-detect branch in the router.
        window.history.replaceState(null, "", window.location.pathname);

        // 2) Commit the user + drop the global loading flag.
        setUser(data);
        if (typeof setLoading === "function") setLoading(false);

        // 3) Pick target route — treat both prod 'active' and dev
        //    'pro_tier' / 'active_business' as paid. We MUST apply the
        //    dev-override here too so a business-tier engineer with an
        //    "inactive" DB row is still routed to /dashboard on sign-in.
        const effective = applyDevOverride(data) || {};
        const paid = new Set(["active", "pro_tier", "active_business"]);
        const target = paid.has(effective.subscription_status) ? "/dashboard" : "/paywall";

        // 4) DETERMINISTIC HARD REDIRECT.
        //    Emergent's session cookie is now set by /auth/session. A hard
        //    location.replace re-boots the SPA at `target` with the cookie
        //    already in place — AuthProvider's mount effect will call
        //    /auth/me and RequireAuth guards resolve immediately. This
        //    exactly matches what a manual refresh does, but automatically.
        window.location.replace(target);
      } catch (e) {
        if (typeof setLoading === "function") setLoading(false);
        window.location.replace("/login?error=session");
      }
    })();
  }, [navigate, setUser, setLoading]);

  return (
    <div className="min-h-screen grid-bg flex items-center justify-center">
      <div className="glass rounded-2xl px-10 py-8 text-center">
        <div className="text-emerald-400 font-mono text-sm tracking-[0.3em] uppercase mb-2">
          Signing you in
        </div>
        <div className="text-slate-300">Establishing secure session…</div>
      </div>
    </div>
  );
}
